import Vue from 'vue'
import Vuex from 'vuex'
import { getEffectivePermissions } from '@/services/api/permission/permission'

Vue.use(Vuex)

const STORAGE_KEY = 'routr_permissions'
// Bump whenever the persisted state shape changes. Pre-bump caches are
// dropped on load. v1 → v2: entry shape extended with `actions` and
// `piiMasked` (May 7, 2026 H1 fix). Anything older is fail-closed —
// store hydrates empty and the next force-fetch on login refills it.
const STORAGE_VERSION = 2

const TIER_RANK = {
  view: 1,
  operate: 2,
  extract: 3,
}

/**
 * Cumulative tier check: extract ⊃ operate ⊃ view.
 */
export function tierSatisfies(granted, required) {
  if (!granted) return false
  return TIER_RANK[granted] >= TIER_RANK[required]
}

const emptyState = () => ({
  userId: null,
  /** Record<resource, { resource, tier, actions, piiMasked, source }> */
  permissions: {},
  loaded: false,
  loading: false,
  error: null,
})

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed?.version !== STORAGE_VERSION) {
      // Stale shape — drop it. Next force-fetch repopulates with current shape.
      localStorage.removeItem(STORAGE_KEY)
      return null
    }
    if (
      parsed.loaded &&
      parsed.userId &&
      parsed.permissions &&
      Object.keys(parsed.permissions).length > 0
    ) {
      return { ...emptyState(), ...parsed, loading: false, error: null }
    }
    return null
  } catch (e) {
    return null
  }
}

const persistToStorage = (state) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        version: STORAGE_VERSION,
        userId: state.userId,
        permissions: state.permissions,
        loaded: state.loaded,
      }),
    )
  } catch (e) {
    /* ignore quota errors */
  }
}

export default {
  namespaced: true,
  state: loadFromStorage() || emptyState(),
  mutations: {
    SET_LOADING(state) {
      state.loading = true
      state.error = null
    },
    SET_LOADED(state, { userId, entries }) {
      state.userId = userId
      const map = {}
      for (const entry of entries || []) {
        map[entry.resource] = entry
      }
      state.permissions = map
      state.loaded = true
      state.loading = false
      state.error = null
      persistToStorage(state)
    },
    SET_ERROR(state, message) {
      state.loading = false
      state.loaded = false
      state.error = message
    },
    CLEAR(state) {
      Object.assign(state, emptyState())
      try {
        localStorage.removeItem(STORAGE_KEY)
      } catch (e) {
        /* ignore */
      }
    },
  },
  actions: {
    async FETCH_PERMISSIONS({ commit, state }, { userId, force = false } = {}) {
      if (!userId) return
      if (state.loading) return
      if (!force && state.loaded && state.userId === userId) return

      // Clear immediately if the persisted state is for a different user — we
      // must not let user B see user A's permissions during the in-flight fetch.
      if (state.loaded && state.userId !== userId) {
        commit('CLEAR')
      }

      commit('SET_LOADING')
      try {
        const entries = await getEffectivePermissions(userId)
        if (!Array.isArray(entries)) {
          throw new Error('Invalid permissions response')
        }
        commit('SET_LOADED', { userId, entries })
      } catch (err) {
        const msg =
          (err && err.response && err.response.data && err.response.data.message) ||
          (err && err.message) ||
          'Failed to load permissions'
        // eslint-disable-next-line no-console
        console.error('[permissions] fetch failed:', msg, err)
        commit('SET_ERROR', msg)
      }
    },
    CLEAR_PERMISSIONS({ commit }) {
      commit('CLEAR')
    },
  },
  getters: {
    loaded: (state) => state.loaded,
    loading: (state) => state.loading,
    error: (state) => state.error,
    /**
     * V1 tier check: can(resource, tier='view') → boolean
     * Kept for backward compat with existing menu/UI code that still
     * uses tier-based gating. Fail-closed: returns false until loaded.
     */
    can: (state) => (resource, tier = 'view') => {
      if (!state.loaded) return false
      const entry = state.permissions[resource]
      if (!entry) return false
      return tierSatisfies(entry.tier, tier)
    },
    canView: (state, getters) => (resource) => getters.can(resource, 'view'),
    canOperate: (state, getters) => (resource) => getters.can(resource, 'operate'),
    canExtract: (state, getters) => (resource) => getters.can(resource, 'extract'),
    /**
     * V2 action check: canAction(resource, action) → boolean
     * Reads from the actions map populated by the backend's V2-shaped
     * effective permissions response. Use this for new code; keep
     * canView/canOperate/canExtract for legacy V1 sites until cleanup.
     */
    canAction: (state) => (resource, action) => {
      if (!state.loaded) return false
      const entry = state.permissions[resource]
      if (!entry || !entry.actions) return false
      return entry.actions[action] === true
    },
    /**
     * piiMasked(resource) → boolean
     * Mirrors the backend PiiMaskingInterceptor decision so the UI can
     * pre-emptively show "PII hidden" placeholders without a round-trip.
     */
    piiMasked: (state) => (resource) => {
      if (!state.loaded) return true
      const entry = state.permissions[resource]
      return entry ? entry.piiMasked === true : true
    },
    /**
     * Portal-level role checks. Use these for portal redirects only;
     * for resource gating always prefer canView/canOperate.
     */
    roleSlug: (state, getters, rootState) => rootState.user && rootState.user.roleSlug,
    isRoutrTeam: (state, getters) => getters.roleSlug === 'routr_team',
    isSuperAdmin: (state, getters) =>
      getters.roleSlug === 'super_admin' || getters.roleSlug === 'routr_team',
    isAdmin: (state, getters) => {
      const slug = getters.roleSlug
      return (
        slug === 'team_admin' ||
        slug === 'admin' ||
        slug === 'super_admin' ||
        slug === 'routr_team'
      )
    },
    isPartner: (state, getters) => {
      const slug = getters.roleSlug
      return (
        slug === 'partner_admin' ||
        slug === 'partner_member' ||
        slug === 'partner_guest' ||
        slug === 'partner'
      )
    },
    isParent: (state, getters) => {
      const slug = getters.roleSlug
      return (
        slug === 'parent_admin' ||
        slug === 'parent_member' ||
        slug === 'parent_guest'
      )
    },
  },
}
